// SPDX-License-Identifier: GPL-3.0-or-later
// Dennis, the guard SigmaBot: kickback guardian (THEME.md section 5.1).
// A box body in coal, a helmet and a badge. He holds up a red stop palm at his side.
// The group faces +z. The table data turns him to face +x.

import { BoxGeometry } from "three";
import { BRAND_HEX } from "../art/canvas.ts";
import { drawTextPanel } from "../art/labels.ts";
import { addBot, botFront } from "./bot.ts";
import {
  type Built,
  type CharacterEnv,
  disposeTree,
  makeLabel,
  motion,
  namedGroup,
  outlined,
  put,
  sec,
  solid,
} from "./common.ts";
import type { SigmaBotSpec } from "./sigmabot/index.ts";

/** The look of Dennis. */
export const DENNIS_SPEC: SigmaBotSpec = {
  body: "box",
  palette: "coal",
  eyes: "angry",
  mouth: "flat",
  hat: "helmet",
  accessory: "badge",
};

/** Size of one body unit of Dennis, in recipe units. */
const SCALE = 1.15;

/** The palm moves 10 units forward in 80 ms and back in 200 ms. */
const PALM_TRAVEL = 10;
const PALM_OUT_MS = 80;
const PALM_BACK_MS = 200;
const PALM_REST_Z = 14;

/** Palm offset (0 to 1) at `elapsedMs` after the kickback started. */
export function palmPhase(elapsedMs: number): number {
  if (elapsedMs < 0) return 0;
  if (elapsedMs < PALM_OUT_MS) return elapsedMs / PALM_OUT_MS;
  if (elapsedMs < PALM_OUT_MS + PALM_BACK_MS) return 1 - (elapsedMs - PALM_OUT_MS) / PALM_BACK_MS;
  return 0;
}

export function buildDennis(env: CharacterEnv): Built {
  const group = namedGroup("Dennis");
  const bot = addBot(group, "DennisBot", DENNIS_SPEC, SCALE);

  // Stop palm: a red fill (the kickback plate), raised at his right side.
  const palm = outlined(
    put(new BoxGeometry(22, 28, 5), solid(BRAND_HEX.mark), 46, 42, PALM_REST_Z),
  );
  palm.name = "DennisPalm";
  const chest = makeLabel(env, 36, 14, 256, 96, (ctx, w, h) => drawTextPanel(ctx, w, h, ["DENY"]));
  chest.mesh.position.set(0, 14, botFront(DENNIS_SPEC, SCALE, 14) + 0.4);

  group.add(palm, chest.mesh);

  let startedAt = Number.NEGATIVE_INFINITY;
  return {
    group,
    tick(timeMs) {
      const a = motion(env);
      bot.rotation.y = 0.15 * Math.sin(sec(timeMs) * 1.2) * a;
      palm.position.z = PALM_REST_Z + PALM_TRAVEL * a * palmPhase(timeMs - startedAt);
    },
    play(name, _arg, timeMs) {
      if (name === "kickback") startedAt = timeMs;
    },
    dispose() {
      disposeTree(group);
    },
  };
}
